import { getGlobalSettingsAccountMailboxInfo } from 'owa-account-source-list-store';
import { logGreyError, logUsage } from 'owa-analytics';
import { isRunningInMetaOSHub } from 'owa-config';
import { setDateFormat, setTimeFormat } from 'owa-datetime-store';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getConfig } from 'owa-service/lib/config';
import type GetRegionalConfigurationResponse from 'owa-service/lib/contract/GetRegionalConfigurationResponse';
import getRegionalConfigurationRequest from 'owa-service/lib/factory/getRegionalConfigurationRequest';
import getMailboxRequestOptions from 'owa-service/lib/getMailboxRequestOptions';
import getRegionalConfigurationOperation from 'owa-service/lib/operation/getRegionalConfigurationOperation';
import { updateUserConfiguration } from 'owa-session-store';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { getItem } from 'owa-local-storage';
import { isMac } from 'owa-user-agent/lib/userAgent';

export const localStorageDateTimeFormatKey = 'TeamsDateTimeFormatSettings';

export type UpdateLocalDateTimeFormatScenario = 'OnLoad' | 'OnFeatureToggle';

/**
 * Update the date and time formats to the host formats only locally for Teams Calendar.
 */
export async function updateLocalDateTimeFormat(
    scenario: UpdateLocalDateTimeFormatScenario
): Promise<void> {
    if (
        !isRunningInMetaOSHub() ||
        !isFeatureEnabled('teams-datetime-format') ||
        getItem(self, localStorageDateTimeFormatKey) == 'Outlook'
    ) {
        return;
    }

    try {
        // Get the host date and time format
        const metaOSContext = getConfig()?.metaOSContext;
        const hostType = metaOSContext?.clientType;

        /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
         * BASELINE: DO NOT COPY/PASTE!
         *	> Argument at index 1 for logUsage must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
        logUsage('UpdateLocalDateTimeFormat', undefined, {
            scenario,
            hostType,
        });

        // Normalize the host formats by replacing U+202F (narrow no-break space) with a regular space (U+0020)
        const hostDateFormat = metaOSContext?.dateFormat?.replace(/\u202F/g, ' ');
        const hostTimeFormat = metaOSContext?.timeFormat?.replace(/\u202F/g, ' ');

        const locale = metaOSContext?.locale;

        if (!hostDateFormat || !hostTimeFormat) {
            /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
             * BASELINE: DO NOT COPY/PASTE!
             *	> Argument at index 1 for logGreyError must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
            logGreyError('LocalDateTimeFormatEmpty', undefined, undefined, {
                dateFormat: hostDateFormat,
                timeFormat: hostTimeFormat,
                locale,
                hostType,
                scenario,
            });
            return;
        }

        let updatedHostDateFormat = hostDateFormat;
        let updatedHostTimeFormat = hostTimeFormat;

        // Convert Mac date/time formats to Windows formats so that they can be recognized by OWA
        if (isMac()) {
            // This conversion only covers the OWA-supported date/time formats
            updatedHostDateFormat = convertMacDateFormat(hostDateFormat);
            updatedHostTimeFormat = convertMacTimeFormat(hostTimeFormat);
        }

        const userOptions = getUserConfiguration()?.UserOptions;
        const shouldUpdateDateFormat = userOptions?.DateFormat !== updatedHostDateFormat;
        const shouldUpdateTimeFormat = userOptions?.TimeFormat !== updatedHostTimeFormat;

        if (!shouldUpdateDateFormat && !shouldUpdateTimeFormat) {
            return;
        }

        // Get supported formats
        const { supportedShortDateFormats, supportedShortTimeFormats } = await getRegionalOptions();

        // Also normalize the entries in the supported format arrays before doing the comparison.
        const newTimeFormat = supportedShortTimeFormats?.find(
            format => format.replace(/\u202F/g, ' ') === updatedHostTimeFormat
        );
        const newDateFormat = supportedShortDateFormats?.find(
            format => format.replace(/\u202F/g, ' ') === updatedHostDateFormat
        );

        if (!newDateFormat || !newTimeFormat) {
            /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
             * BASELINE: DO NOT COPY/PASTE!
             *	> Argument at index 1 for logGreyError must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
            logGreyError('LocalDateTimeFormatInvalid', undefined, undefined, {
                dateFormat: newDateFormat,
                timeFormat: newTimeFormat,
                numSupportedDateFormats: supportedShortDateFormats?.length ?? -1,
                numSupportedTimeFormats: supportedShortTimeFormats?.length ?? -1,
                hostDateFormat,
                hostTimeFormat,
                updatedHostDateFormat,
                updatedHostTimeFormat,
                scenario,
                locale,
            });

            // Temporary log for debugging purposes; will be removed after investigation.

            /* eslint-disable-next-line owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
             * BASELINE: DO NOT COPY/PASTE!
             *	> Argument at index 1 for logGreyError must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
            logGreyError(
                'VerboseLocalDateTimeFormatInvalid',
                undefined,
                undefined,
                {
                    dateFormat: newDateFormat,
                    timeFormat: newTimeFormat,
                    numSupportedDateFormats: supportedShortDateFormats?.length ?? -1,
                    numSupportedTimeFormats: supportedShortTimeFormats?.length ?? -1,
                    hostDateFormat,
                    hostTimeFormat,
                    updatedHostDateFormat,
                    updatedHostTimeFormat,
                    scenario,
                    locale,
                    supportedShortDateFormats: supportedShortDateFormats?.join(', '),
                    supportedShortTimeFormats: supportedShortTimeFormats?.join(', '),
                    hmmttIncluded:
                        supportedShortTimeFormats?.includes('h:mm tt') ||
                        supportedShortTimeFormats?.includes('h:mm\u202Ftt'), // check whether the supported formats include 'h:mm tt' (with regular or narrow no-break space),
                },
                { ring: 'Dogfood' }
            );

            return;
        }

        if (shouldUpdateDateFormat) {
            setDateFormat(newDateFormat);
        }

        if (shouldUpdateTimeFormat) {
            setTimeFormat(newTimeFormat);
        }

        // Update UserOptions
        updateUserConfiguration(config => {
            if (config.UserOptions) {
                if (shouldUpdateDateFormat) {
                    config.UserOptions.DateFormat = newDateFormat;
                }
                if (shouldUpdateTimeFormat) {
                    config.UserOptions.TimeFormat = newTimeFormat;
                }
            }
        });
    } catch (error) {
        /* eslint-disable owa-custom-rules/telemetry-mailboxinfo-as-second-param -- (https://aka.ms/OWALintWiki)
         * BASELINE: DO NOT COPY/PASTE!
         *	> Argument at index 1 for logGreyError must be of type MailboxInfo and not undefined. Telemetry calls that violate this rule will eventually be removed by the framework team. https://aka.ms/owa-analytics-mailboxinfo */
        // Errors resulting from enabling useUnknownInCatchVariables
        // TS2345: Argument of type 'unknown' is not assignable to parameter of type 'Error | undefined'.
        // @ts-expect-error
        logGreyError('LocalDateTimeFormatError', undefined, error, {
            /* eslint-enable owa-custom-rules/telemetry-mailboxinfo-as-second-param */
            scenario,
        });
        return;
    }
}

/**
 * Gets the regional configuration from the server.
 * @returns The supported date and time formats.
 */
async function getRegionalOptions(): Promise<{
    supportedShortDateFormats: string[] | undefined;
    supportedShortTimeFormats: string[] | undefined;
}> {
    const regionalConfig = await getRegionalConfiguration();
    const supportedShortDateFormats = regionalConfig.SupportedShortDateFormats;
    const supportedShortTimeFormats = regionalConfig.SupportedShortTimeFormats;

    return {
        supportedShortDateFormats,
        supportedShortTimeFormats,
    };
}

function getRegionalConfiguration(): Promise<GetRegionalConfigurationResponse> {
    return getRegionalConfigurationOperation(
        getRegionalConfigurationRequest({}),
        getMailboxRequestOptions(getGlobalSettingsAccountMailboxInfo())
    );
}

const explicitTimeFormatMapping: Record<string, string> = {
    // 24-hour (with different format)
    'HHH:mm': "HH'H'mm",
    'HH h mm': "HH' h 'mm",
    'HHh:mm': "HH'h'mm",

    // Literal suffixes
    "HH:mm 'Uhr'": "HH:mm' Uhr'",
    "H.mm 'Auer'": "H.mm' Auer'",
    "H.mm 'u.'": "H.mm' u.'",
    "HH.mm 'uur'": "HH.mm' uur'",
    "HH:mm 'uur'": "HH:mm' uur'",
    "'kl' H:mm": "'kl 'H:mm",

    // Compact AM/PM case that doesn't follow the general pattern
    'ah:mm': 'tth:mm',
};

const explicitDateFormatMapping: Record<string, string> = {
    'yyyy/MM/dd/dddd': 'yyyy/MM/dd',
};

function convertMacDateFormat(macDateFormat: string): string {
    // Check explicit mapping first
    if (explicitDateFormatMapping[macDateFormat]) {
        return explicitDateFormatMapping[macDateFormat];
    }
    // Replace single 'y' with 'yy', but avoid replacing 'yyyy', 'yyy', or 'yy'
    return macDateFormat.replace(/(^|[^y])y([^y]|$)/g, '$1yy$2');
}

function convertMacTimeFormat(macTimeFormat: string): string {
    // Check explicit mapping first
    if (explicitTimeFormatMapping[macTimeFormat]) {
        return explicitTimeFormatMapping[macTimeFormat];
    }
    // Only replace standalone 'a' (not inside words)
    return macTimeFormat.replace(/\ba\b/g, 'tt');
}
